import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { crm, Pill } from "../../../../theme";
import { TaxAttrOption, TaxCombo, TaxSelection, TaxTreatment, TaxTreatmentSet } from "../../../../models/TaxTreatment";
import { getTaxSelection, getTaxTreatments, saveTaxSelection } from "../../../../services/taxTreatmentService";
import { formatNcm } from "../../../process/components/tableStyles";

const labelProps = { fontSize: "12px", fontWeight: "600", color: crm.textMuted, mb: "4px" };
const inputProps = { h: "42px", borderRadius: "10px", focusBorderColor: crm.brand };

const keyOf = (t: TaxTreatment) => `${t.tributo}|${t.regimeCode}|${t.fundamentoCode}`;

type Picks = Record<string, string>; // código do atributo (valor fixo) -> valor escolhido
type TreatmentState = { picks: Picks; free: Record<string, string> };
const EMPTY: TreatmentState = { picks: {}, free: {} };

/**
 * Níveis de escolha: a cada posição de atributo, opções (valor fixo) entre as combinações ainda compatíveis com as escolhas anteriores.
 * Ex.: ANTIDUMPING = "Incide?" Sim/Não -> (Sim) produto -> produtor. "Não" é combinação completa com 1 atributo.
 */
const buildLevels = (t: TaxTreatment, picks: Picks) => {
  let candidates: TaxCombo[] = t.combos;
  const levels: { code: string; label: string; options: { value: string; label: string }[]; picked?: string }[] = [];
  for (let idx = 0; idx < 12; idx++) {
    const withAttr = candidates.filter((c) => c.attributes[idx] && c.attributes[idx].value !== "");
    if (withAttr.length === 0) break;
    const first = withAttr[0].attributes[idx];
    const seen = new Map<string, string>();
    withAttr.forEach((c) => {
      const a = c.attributes[idx];
      if (!seen.has(a.value)) seen.set(a.value, a.valueLabel || a.value);
    });
    const picked = picks[first.code] && seen.has(picks[first.code]) ? picks[first.code] : undefined;
    levels.push({
      code: first.code,
      label: first.label || first.code,
      options: Array.from(seen, ([value, label]) => ({ value, label })),
      picked,
    });
    if (picked === undefined) return { levels, complete: false, combo: undefined as TaxCombo | undefined };
    candidates = candidates.filter((c) => c.attributes[idx] && c.attributes[idx].value === picked);
  }
  return { levels, complete: candidates.length > 0, combo: candidates[0] };
};

const LevelSelect = ({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value?: string;
  onChange: (v: string) => void;
}) => {
  const [q, setQ] = useState("");
  const filtered = q.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()) || o.value === value)
    : options;
  return (
    <Box mb="12px">
      <Text {...labelProps}>{label}</Text>
      {options.length > 8 && (
        <Input {...inputProps} mb="6px" placeholder="Buscar..." value={q} onChange={(e) => setQ(e.target.value)} />
      )}
      <Select {...inputProps} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Selecione">
        {filtered.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </Box>
  );
};

const toSelection = (t: TaxTreatment, st: TreatmentState): TaxSelection | undefined => {
  const { complete, combo } = buildLevels(t, st.picks);
  if (!complete || !combo) return undefined;
  const freeFilled = combo.attributes.some((a) => a.value === "" && (st.free[a.code] ?? "").trim() !== "");
  const anyPick = Object.keys(st.picks).length > 0;
  if (!t.required && !anyPick && !freeFilled) return undefined;
  const atributos: { codigo: string; valor: string }[] = [];
  combo.attributes.forEach((a: TaxAttrOption) => {
    const v = a.value !== "" ? a.value : (st.free[a.code] ?? "").trim();
    if (v !== "") atributos.push({ codigo: a.code, valor: v });
  });
  return { tributo: t.tributo!, regime: t.regimeCode, fundamento: t.fundamentoCode, atributos };
};

const fromSelection = (t: TaxTreatment, s?: TaxSelection): TreatmentState => {
  const st: TreatmentState = { picks: {}, free: {} };
  if (!s) return st;
  const fixedCodes = new Set(t.combos.flatMap((c) => c.attributes.filter((a) => a.value !== "").map((a) => a.code)));
  s.atributos.forEach((a) => {
    if (fixedCodes.has(a.codigo)) st.picks[a.codigo] = a.valor;
    else st.free[a.codigo] = a.valor;
  });
  return st;
};

/**
 * Tributos (TTCE) do item: enquadramento por tributo (ex.: ANTIDUMPING 3/1/1300 com produtor) para ItemCover.tributos.
 * Chave de armazenamento: NCM + produto + exportador + país (o antidumping depende do produtor).
 */
const TaxTreatmentModal = ({
  isOpen,
  onClose,
  ncm,
  product,
  exporter,
  country,
}: {
  isOpen: boolean;
  onClose: () => void;
  ncm: string;
  product: string;
  exporter: string;
  country: string;
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [set, setSet] = useState<TaxTreatmentSet>();
  const [state, setState] = useState<Record<string, TreatmentState>>({});

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    Promise.all([getTaxTreatments(ncm, country), getTaxSelection(ncm, product, exporter, country)])
      .then(([s, saved]) => {
        if (cancelled) return;
        setSet(s);
        const init: Record<string, TreatmentState> = {};
        s.treatments.forEach((t) => {
          if (!t.tributo) return;
          init[keyOf(t)] = fromSelection(
            t,
            saved.find((x) => x.tributo === t.tributo && x.regime === t.regimeCode && x.fundamento === t.fundamentoCode)
          );
        });
        setState(init);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const msg = e?.response?.data?.error;
        setError(typeof msg === "string" ? msg : "Não foi possível consultar os tratamentos tributários (TTCE).");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, ncm, product, exporter, country]);

  const treatments = useMemo(() => (set?.treatments ?? []).filter((t) => t.tributo && t.combos.length > 0), [set]);
  const pending = treatments.filter((t) => t.required && !toSelection(t, state[keyOf(t)] ?? EMPTY));

  const patch = (t: TaxTreatment, fn: (s: TreatmentState) => TreatmentState) =>
    setState((cur) => ({ ...cur, [keyOf(t)]: fn(cur[keyOf(t)] ?? EMPTY) }));

  const pick = (t: TaxTreatment, code: string, value: string) =>
    patch(t, (s) => {
      // trocar um nível descarta as escolhas dos níveis seguintes
      const { levels } = buildLevels(t, s.picks);
      const idx = levels.findIndex((l) => l.code === code);
      const picks: Picks = {};
      levels.slice(0, idx).forEach((l) => {
        if (l.picked !== undefined) picks[l.code] = l.picked;
      });
      if (value) picks[code] = value;
      return { ...s, picks };
    });

  const save = async () => {
    const selections = treatments
      .map((t) => toSelection(t, state[keyOf(t)] ?? EMPTY))
      .filter((x): x is TaxSelection => !!x);
    setSaving(true);
    try {
      await saveTaxSelection(ncm, product, exporter, country, selections);
      toast({ title: "Tributos salvos", status: "success" });
      onClose();
    } catch (e: any) {
      const data = e?.response?.data;
      toast({ title: "Não foi possível salvar os tributos", description: typeof data === "string" ? data : undefined, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" boxShadow={crm.shadowLg}>
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Text fontSize="18px" fontWeight="700" color={crm.text}>
            Tributos (TTCE)
          </Text>
          <Text fontSize="13px" fontWeight="400" color={crm.textMuted}>
            {formatNcm(ncm.replace(/\D/g, ""))} · {product || "sem descrição"} · {exporter || "sem exportador"} · {country || "sem país"}
            {set && pending.length > 0 && ` · ${pending.length} pendente(s)`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="20px">
          {loading ? (
            <Flex justify="center" py="40px">
              <Spinner color={crm.brand} />
            </Flex>
          ) : error ? (
            <Text fontSize="13px" color={crm.danger}>
              {error}
            </Text>
          ) : treatments.length === 0 ? (
            <Text fontSize="13px" color={crm.textFaint}>
              Este NCM/país não tem tratamento tributário com campos adicionais.
            </Text>
          ) : (
            <Flex direction="column" gap="16px">
              {treatments.map((t) => {
                const st = state[keyOf(t)] ?? EMPTY;
                const { levels, complete, combo } = buildLevels(t, st.picks);
                const freeAttrs = complete && combo ? combo.attributes.filter((a) => a.value === "") : [];
                const auto = t.combos.length === 1 && t.required;
                return (
                  <Box key={keyOf(t)} border={`1px solid ${crm.border}`} borderRadius={crm.radius} p="16px">
                    <Flex align="center" gap="8px" mb="10px" wrap="wrap">
                      <Text fontSize="14px" fontWeight="700" color={crm.text}>
                        {t.tributoName}
                      </Text>
                      <Text fontSize="12px" color={crm.textMuted}>
                        {t.regimeName} · {t.fundamentoCode} {t.fundamentoName} ({t.tipo})
                      </Text>
                      {t.required ? (
                        <Pill bg={crm.brandSoft} color={crm.brand}>
                          {auto ? "automático" : "obrigatório"}
                        </Pill>
                      ) : (
                        <Pill>opcional</Pill>
                      )}
                    </Flex>
                    {levels.map((l) => (
                      <LevelSelect key={l.code} label={l.label} options={l.options} value={l.picked} onChange={(v) => pick(t, l.code, v)} />
                    ))}
                    {freeAttrs.map((a) => (
                      <Box key={a.code} mb="8px">
                        <Text {...labelProps}>
                          {a.label || a.code} ({a.type || "valor livre"}, opcional)
                        </Text>
                        <Input
                          {...inputProps}
                          inputMode="decimal"
                          value={st.free[a.code] ?? ""}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9.]/g, "");
                            patch(t, (s) => ({ ...s, free: { ...s.free, [a.code]: v } }));
                          }}
                          placeholder="ex.: 0.00"
                        />
                      </Box>
                    ))}
                  </Box>
                );
              })}
              {set && set.optional.length > 0 && (
                <Text fontSize="12px" color={crm.textFaint}>
                  {set.optional.length} fundamentos opcionais disponíveis no TTCE (ainda não suportados aqui).
                </Text>
              )}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`} gap="8px">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isLoading={saving} isDisabled={loading || !!error} onClick={save}>
            Salvar tributos
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TaxTreatmentModal;
